import { computed, ref, watch } from 'vue';

import type { Ref } from 'vue';

export type UseImagePreviewNavigationOptions = {
  /**
   * The image sources being previewed. Navigation resets to the first image
   * whenever the set changes.
   */
  images: Ref<string[]>;
};

export type UseImagePreviewNavigationReturn = {
  currentIndex: Ref<number>;
  currentImage: Ref<string | null>;
  total: Ref<number>;
  hasPrevious: Ref<boolean>;
  hasNext: Ref<boolean>;
  goToIndex: (index: number) => void;
  goToPrevious: () => void;
  goToNext: () => void;
};

/**
 * Index state for a set of previewed images: clamping and reset. Kept separate
 * from the preview shell so consumers can drive navigation from their own
 * controls (arrow buttons, a timeline, thumbnails, keyboard).
 */
export function useImagePreviewNavigation({
  images
}: UseImagePreviewNavigationOptions): UseImagePreviewNavigationReturn {
  const currentIndex = ref(0);

  const total = computed(() => images.value.length);
  const currentImage = computed(() => images.value[currentIndex.value] ?? null);

  const hasPrevious = computed(() => currentIndex.value > 0);
  const hasNext = computed(() => currentIndex.value < Math.max(total.value - 1, 0));

  const goToIndex = (index: number) => {
    if (total.value === 0) {
      currentIndex.value = 0;
      return;
    }

    // Ignore NaN, fractional and infinite values so currentImage never resolves
    // to an out-of-band slot; valid (incl. negative) integers still clamp below.
    if (!Number.isInteger(index)) {
      return;
    }

    currentIndex.value = Math.min(Math.max(index, 0), total.value - 1);
  };

  const goToPrevious = () => {
    if (!hasPrevious.value) {
      return;
    }

    goToIndex(currentIndex.value - 1);
  };

  const goToNext = () => {
    if (!hasNext.value) {
      return;
    }

    goToIndex(currentIndex.value + 1);
  };

  watch(images, () => {
    currentIndex.value = 0;
  });

  return {
    currentIndex,
    currentImage,
    total,
    hasPrevious,
    hasNext,
    goToIndex,
    goToPrevious,
    goToNext
  };
}
